import PropTypes from 'prop-types';

import {
  PaginationWrapper,
  PaginationPageNumberItem,
  PaginationDotsItem,
} from './Pagination.style';

// ###############################################################
// <---------------------- Sub Sections ------------------------->
// ###############################################################
const CurrentPage = ({ pageNumber }) => (
  <PaginationPageNumberItem selected>{pageNumber}</PaginationPageNumberItem>
);
CurrentPage.propTypes = {
  pageNumber: PropTypes.number.isRequired,
};

const StartingDots = ({ handlePaginationClick }) => (
  <>
    <PaginationPageNumberItem onClick={() => handlePaginationClick(1)}>1</PaginationPageNumberItem>
    <PaginationDotsItem>...</PaginationDotsItem>
  </>
);
StartingDots.propTypes = {
  handlePaginationClick: PropTypes.func.isRequired,
};

const EndingDots = ({ pageNumber, handlePaginationClick }) => (
  <>
    <PaginationDotsItem>...</PaginationDotsItem>
    <PaginationPageNumberItem onClick={() => handlePaginationClick(pageNumber)}>
      {pageNumber}
    </PaginationPageNumberItem>
  </>
);
EndingDots.propTypes = {
  pageNumber: PropTypes.number.isRequired,
  handlePaginationClick: PropTypes.func.isRequired,
};

const PrePages = ({
  prePageStartIndex,
  prePageEndIndex,
  preShiftAmount,
  handlePaginationClick,
}) => {
  const pages = [];

  for (let i = prePageStartIndex - preShiftAmount; i <= prePageEndIndex; i += 1) {
    if (i > 0)
      pages.push(
        <PaginationPageNumberItem key={i} onClick={() => handlePaginationClick(i)}>
          {i}
        </PaginationPageNumberItem>,
      );
  }

  return <>{pages.map(item => item)}</>;
};
PrePages.propTypes = {
  prePageStartIndex: PropTypes.number.isRequired,
  prePageEndIndex: PropTypes.number.isRequired,
  preShiftAmount: PropTypes.number.isRequired,
  handlePaginationClick: PropTypes.func.isRequired,
};

const PostPages = ({
  totalCount,
  postPageStartIndex,
  postPageEndIndex,
  postShiftAmount,
  handlePaginationClick,
}) => {
  const pages = [];

  for (let i = postPageStartIndex; i <= postPageEndIndex + postShiftAmount; i += 1) {
    if (i <= totalCount)
      pages.push(
        <PaginationPageNumberItem key={i} onClick={() => handlePaginationClick(i)}>
          {i}
        </PaginationPageNumberItem>,
      );
  }

  return <>{pages.map(item => item)}</>;
};
PostPages.propTypes = {
  totalCount: PropTypes.number.isRequired,
  postPageStartIndex: PropTypes.number.isRequired,
  postPageEndIndex: PropTypes.number.isRequired,
  postShiftAmount: PropTypes.number.isRequired,
  handlePaginationClick: PropTypes.func.isRequired,
};
// ###############################################################
// <-------------------- End Sub Sections ----------------------->
// ###############################################################

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_DOTS = 8;
const NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_FIRST_AND_CURRENT_PAGES = 3;
const NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_CURRENT_AND_LAST_PAGES = 4;
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const Pagination = ({ currentPage, totalCount, handlePaginationClick }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  let prePageStartIndex = 1;
  const prePageEndIndex = currentPage - 1;

  if (
    prePageEndIndex - prePageStartIndex >=
      NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_FIRST_AND_CURRENT_PAGES &&
    totalCount > NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_DOTS
  )
    prePageStartIndex = currentPage - NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_FIRST_AND_CURRENT_PAGES;
  const numberOfPrePages = prePageEndIndex - prePageStartIndex + 1;
  const postShiftAmount = Math.max(
    NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_FIRST_AND_CURRENT_PAGES - numberOfPrePages,
    0,
  );

  const postPageStartIndex = currentPage + 1;
  let postPageEndIndex = totalCount;

  if (
    totalCount - postPageStartIndex >= NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_CURRENT_AND_LAST_PAGES &&
    totalCount > NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_DOTS
  )
    postPageEndIndex =
      postPageStartIndex + NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_CURRENT_AND_LAST_PAGES - 1;
  const numberOfPostPages = postPageEndIndex - postPageStartIndex + 1;
  const preShiftAmount = Math.max(
    NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_CURRENT_AND_LAST_PAGES - numberOfPostPages,
    0,
  );
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <PaginationWrapper data-testid="pagination-wrapper">
      {prePageStartIndex > 1 && <StartingDots handlePaginationClick={handlePaginationClick} />}
      <PrePages
        prePageStartIndex={prePageStartIndex}
        prePageEndIndex={prePageEndIndex}
        preShiftAmount={preShiftAmount}
        handlePaginationClick={handlePaginationClick}
      />
      <CurrentPage pageNumber={currentPage} />
      <PostPages
        totalCount={totalCount}
        postPageStartIndex={postPageStartIndex}
        postPageEndIndex={postPageEndIndex}
        postShiftAmount={postShiftAmount}
        handlePaginationClick={handlePaginationClick}
      />
      {postPageStartIndex +
        postShiftAmount +
        NUMBER_OF_PAGES_TO_APPEAR_BETWEEN_CURRENT_AND_LAST_PAGES -
        1 <
        totalCount && (
        <EndingDots pageNumber={totalCount} handlePaginationClick={handlePaginationClick} />
      )}
    </PaginationWrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  handlePaginationClick: PropTypes.func.isRequired,
};

export default Pagination;
